import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  MediaCategoryTypeNameEnum,
  MediaTypeNameEnum,
  MediaViewModel,
  PriceViewModel,
  ProductActionEnum,
  ProductRequestModel,
  ShoppingCartClient,
  ShoppingCartItemViewModel,
  ShoppingCartViewModel,
  UpdateShoppingCartRequestModel,
  VerifyShoppingCartRequestModel,
} from "src/domain/client";
import { TOAST_TYPE } from "src/domain/extentions/toast";
import { MediaPipe } from "src/pipes/media.pipe";
import { ToastService } from "./toast.service";
import { TrackingService } from "./tracking.service";
import { nonNullish } from "src/guards";
import { ErrorReporterService } from "./error-reporter.service";
import { EcomProductArrayPipe } from "src/pipes/ecom-product.pipe";

@Injectable({
  providedIn: "root",
})
export class ShoppingCartService {
  shoppingCart?: ShoppingCartViewModel;
  productExceptions: string[] = [];
  campaignCodeId?: number;

  constructor(
    private toastService: ToastService,
    private errorReporter: ErrorReporterService,
    private translateService: TranslateService,
    private mediaPipe: MediaPipe,
    private trackingService: TrackingService,
    private ecomProductArrayPipe: EcomProductArrayPipe,
  ) {}

  setShoppingCart(shoppingCart: ShoppingCartViewModel | undefined): void {
    const oldCart = this.shoppingCart ?? null;
    this.shoppingCart = shoppingCart;

    this.verifyShoppingCart();
    this.trackShoppingCart(oldCart, shoppingCart ?? null);
  }

  addShoppingCartItem(
    productId: number,
    sku: string,
    minimumQuantity: number | null | undefined,
    media: MediaViewModel[] | null | undefined,
    quantity: number,
  ): void {
    const existingProduct = this.getShoppingCartItemByProductId(productId);

    const productRequestModel = new ProductRequestModel({
      sku,
      quantity: !existingProduct ? minimumQuantity || quantity : quantity,
      action: ProductActionEnum.Add,
    });

    this.updateShoppingCart(
      productRequestModel,
      this.mediaPipe.transform(
        media,
        MediaCategoryTypeNameEnum.ProductName,
        MediaTypeNameEnum.Text,
      ) || sku,
    );
  }

  decreaseQuantity(shoppingCartItemId: number): void {
    const existingProduct = this.getShoppingCartItem(shoppingCartItemId);

    if (existingProduct?.product?.sku == null) {
      return;
    }
    const productRequestModel = new ProductRequestModel({
      sku: existingProduct.product.sku,
      quantity: 1,
      action: ProductActionEnum.Delete,
    });

    this.updateShoppingCart(
      productRequestModel,
      this.mediaPipe.transform(
        existingProduct.product.media,
        MediaCategoryTypeNameEnum.ProductName,
        MediaTypeNameEnum.Text,
      ),
    );
  }

  deleteItem(shoppingCartItemId: number): void {
    const existingProduct = this.getShoppingCartItem(shoppingCartItemId);

    if (existingProduct?.product?.sku == null) {
      return;
    }

    const productRequestModel = new ProductRequestModel({
      sku: existingProduct.product.sku,
      quantity: existingProduct.quantity,
      action: ProductActionEnum.Delete,
    });

    this.updateShoppingCart(
      productRequestModel,
      this.mediaPipe.transform(
        existingProduct.product.media,
        MediaCategoryTypeNameEnum.ProductName,
        MediaTypeNameEnum.Text,
      ),
    );
  }

  clearShoppingCart(): void {
    const shoppingCartId = this.shoppingCart?.shoppingCartId;
    if (shoppingCartId == null) {
      this.errorReporter.handle(new Error("shoppingCartId is null"));
      return;
    }
    new ShoppingCartClient()
      .clear(shoppingCartId)
      .then(() => {
        this.getShoppingCart(shoppingCartId);

        this.toastService.createToast(
          this.translateService.instant("shoppingCart.cleared"),
          TOAST_TYPE.SUCCESS,
        );
      })
      .catch(this.errorReporter.handle);
  }

  private cartItemHasCalculeablePrice(
    p: ShoppingCartItemViewModel,
  ): p is ShoppingCartItemViewModel & {
    product: {
      price: {
        salesPrice: NonNullable<PriceViewModel["salesPrice"]>;
      };
    };
    quantity: NonNullable<ShoppingCartItemViewModel["quantity"]>;
  } {
    return p.product?.price?.salesPrice != null && p.quantity != null;
  }

  private cartItemHasQuantity(
    p: ShoppingCartItemViewModel,
  ): p is ShoppingCartItemViewModel & {
    quantity: NonNullable<ShoppingCartItemViewModel["quantity"]>;
  } {
    return p.product?.price?.salesPrice != null && p.quantity != null;
  }

  // could be a pipe
  get total(): number {
    return (
      this.shoppingCart?.shoppingCartItems
        ?.filter(this.cartItemHasCalculeablePrice, this)
        .reduce((a, p) => a + p.product.price.salesPrice * p.quantity, 0) ?? 0
    );
  }

  // could be a pipe
  get monthlyTotal(): number {
    return (
      this.shoppingCart?.shoppingCartItems
        ?.filter(this.cartItemHasCalculeablePrice, this)
        .reduce(
          (a, p) =>
            a + Math.round((p.product.price.salesPrice * p.quantity) / 12),
          0,
        ) ?? 0
    );
  }

  // could be a pipe
  get totalProductCount(): number {
    return (
      this.shoppingCart?.shoppingCartItems
        ?.filter(this.cartItemHasQuantity, this)
        .reduce((a, p) => a + p.quantity, 0) ?? 0
    );
  }

  get hasItems(): boolean {
    return !!(
      this.shoppingCart &&
      this.shoppingCart.shoppingCartItems &&
      this.shoppingCart.shoppingCartItems.length
    );
  }

  //////////

  private getShoppingCartItem(
    shoppingCartItemId: number,
  ): ShoppingCartItemViewModel | undefined {
    return this.shoppingCart?.shoppingCartItems?.find(
      (i) => i.shoppingCartItemId === shoppingCartItemId,
    );
  }

  private getShoppingCartItemByProductId(
    productId: number,
  ): ShoppingCartItemViewModel | undefined {
    return this.shoppingCart?.shoppingCartItems?.find(
      (i) => i.product?.productId === productId,
    );
  }

  private updateShoppingCart(
    productRequestModel: ProductRequestModel,
    productName: string,
  ): Promise<void> {
    const shoppingCartId = this.shoppingCart?.shoppingCartId;
    if (!shoppingCartId) {
      throw new Error("No shopping cart ID to update");
    }
    const requestModel = new UpdateShoppingCartRequestModel({
      shoppingCartId,
      products: [productRequestModel],
    });

    return new ShoppingCartClient()
      .update(requestModel)
      .then(() => this.getShoppingCart(shoppingCartId))
      .then(() => {
        this.toastService.createToast(
          this.translateService.instant(
            `shoppingCart.actions.${productRequestModel.action}`,
            { quantity: productRequestModel.quantity, productName },
          ),
          TOAST_TYPE.SUCCESS,
        );
      })
      .catch(this.errorReporter.handle);
  }

  private getShoppingCart(shoppingCartId: string): Promise<void> {
    return new ShoppingCartClient()
      .shoppingCart(shoppingCartId)
      .then((shoppingCart: ShoppingCartViewModel) => {
        this.setShoppingCart(shoppingCart);
      })
      .catch(this.errorReporter.handle);
  }

  private trackShoppingCart(
    from: ShoppingCartViewModel | null,
    to: ShoppingCartViewModel | null,
  ): void {
    if (
      to == null ||
      from == null ||
      to.shoppingCartId !== from.shoppingCartId
    ) {
      return;
    }

    const { removed, added } = this.ecomProductArrayPipe
      .transform(from)
      .diffTo(this.ecomProductArrayPipe.transform(to));

    if (removed.length) {
      this.trackingService.trackRemoveFromCartEcom(removed);
    }
    if (added.length) {
      this.trackingService.trackAddToCartEcom(added);
    }
  }

  private verifyShoppingCart(): void {
    const request = new VerifyShoppingCartRequestModel({
      shoppingCartId: this.shoppingCart?.shoppingCartId,
    });

    new ShoppingCartClient()
      .verify(request)
      .then((productExceptionResponses) => {
        this.productExceptions = productExceptionResponses
          .map((_) => _.sku)
          .filter(nonNullish);
      })
      .catch((err) => {
        this.errorReporter.handle(err);

        this.productExceptions = [];
      });
  }
}
